import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Wind, Mail, Phone, Clock, MapPin, Send, Loader2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Using Formspree to send emails to info@madtoys.in
      const response = await fetch('https://formspree.io/f/meepjblv', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          subject: formData.subject,
          message: formData.message,
          _replyto: formData.email,
        }),
      });

      if (response.ok) {
        setSubmitStatus({
          type: 'success',
          message: 'Thank you for contacting us! We will get back to you within 24 hours.'
        });
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: 'Failed to send message. Please try again or email us directly at info@madtoys.in'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-dark-primary">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-dark-primary/80 backdrop-blur-lg border-b border-gold/10">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Wind className="w-8 h-8 text-gold" />
            <span className="text-2xl font-bold text-gold">MADTOYS</span>
          </Link>
          <Link to="/" className="text-gray-300 hover:text-gold transition-colors">
            ← Back to Home
          </Link>
        </div>
      </nav>

      {/* Content */}
      <div className="pt-32 pb-24">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="text-center mb-12">
            <h1 className="text-5xl font-bold text-white mb-6">Get in Touch</h1>
            <p className="text-xl text-gray-400">We'd love to hear from you. Send us a message and we'll respond as soon as possible.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Contact Form */}
            <Card className="bg-dark-secondary border-gold/20">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Send us a Message</h2>
                
                {submitStatus && (
                  <div className={`mb-4 p-4 rounded-lg ${
                    submitStatus.type === 'success' 
                      ? 'bg-green-500/20 border border-green-500/30 text-green-400' 
                      : 'bg-red-500/20 border border-red-500/30 text-red-400'
                  }`}>
                    {submitStatus.message}
                  </div>
                )}
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-gray-300 mb-2">Your Name *</label>
                    <Input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="bg-dark-primary border-gray-700 text-white focus:border-gold"
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-300 mb-2">Email Address *</label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="bg-dark-primary border-gray-700 text-white focus:border-gold"
                      placeholder="john@example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-300 mb-2">Phone Number</label>
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="bg-dark-primary border-gray-700 text-white focus:border-gold"
                      placeholder="+91 XXXXXXXXXX"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-300 mb-2">Subject *</label>
                    <Input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="bg-dark-primary border-gray-700 text-white focus:border-gold"
                      placeholder="How can we help?"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-300 mb-2">Message *</label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="bg-dark-primary border-gray-700 text-white focus:border-gold"
                      placeholder="Tell us more about your inquiry..."
                    />
                  </div>
                  <Button type="submit" disabled={isSubmitting} className="w-full bg-gold hover:bg-gold-dark text-dark-primary font-bold">
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Contact Information */}
            <div className="space-y-6">
              <Card className="bg-dark-secondary border-gold/20">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-gold/10 rounded-lg">
                      <Mail className="w-6 h-6 text-gold" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-2">Email Us</h3>
                      <p className="text-gray-300">info@madtoys.in</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-dark-secondary border-gold/20">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-gold/10 rounded-lg">
                      <Phone className="w-6 h-6 text-gold" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-2">Call Us</h3>
                      <p className="text-gray-300">+91-9137044474</p>
                      <p className="text-sm text-gray-400 mt-1">Mon-Sat, 10 AM - 6 PM IST</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gold/10 border-gold/30">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-gold/20 rounded-lg">
                      <Phone className="w-6 h-6 text-gold" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gold mb-2">Bulk Orders & Wholesale</h3>
                      <p className="text-gray-300">+91-8237530525</p>
                      <p className="text-sm text-gray-400 mt-1">For corporate gifts and bulk purchases</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-dark-secondary border-gold/20">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-gold/10 rounded-lg">
                      <Clock className="w-6 h-6 text-gold" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-2">Business Hours</h3>
                      <p className="text-gray-300">Monday - Saturday</p>
                      <p className="text-gray-300">10:00 AM - 6:00 PM IST</p>
                      <p className="text-sm text-gold mt-2">Closed on Sundays & Public Holidays</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-dark-secondary border-gold/20">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-gold/10 rounded-lg">
                      <MapPin className="w-6 h-6 text-gold" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-2">Visit Us</h3>
                      <p className="text-gray-300">MADTOYS</p>
                      <p className="text-gray-300">India</p>
                      <p className="text-sm text-gray-400 mt-2">madtoys.in</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gold/10 border-gold/30">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-gold mb-2">Bulk Orders & Corporate Gifts</h3>
                  <p className="text-gray-300 text-sm mb-3">
                    Looking to place a bulk order or need corporate gifting solutions? Contact our bulk sales team:
                  </p>
                  <div className="space-y-2">
                    <p className="text-white font-semibold">📞 +91-8237530525</p>
                    <p className="text-gray-400 text-sm">Available Mon-Sat, 10 AM - 6 PM IST</p>
                    <p className="text-gray-400 text-sm mt-2">Special pricing available for orders of 10+ units</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-dark-secondary border-gold/20">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-white mb-2">Quick Response</h3>
                  <p className="text-gray-300 text-sm">
                    We typically respond to all inquiries within 24 hours during business days. 
                    For urgent matters, please call us directly.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactUs;
